import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DropDownList } from "@progress/kendo-react-dropdowns"
import { Switch, TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Dialog } from "@progress/kendo-react-dialogs";
import { plusIcon, saveIcon, trashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { loadingPanel } from "../../LoadingPanel";

export const ConfigRulesAlarmActionHandlers = () => {
    type AlarmActionHandler = {
        id: number;
        name: string;
        mac: string;
        ipAddress: string;
        type: number;
    };

    const localizationService = useLocalization();
    let languageId = localStorage.getItem('languageId');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const COLORS = {
        primary: '#018CA5',
        accent: '#198793',
        selectedBg: '#198792',
        panelBg: '#F7F7F7',
        title: '#1A8893',
        white: '#ffffff',
        markerRed: '#FF0000',
        borderStrong: 'rgba(26, 136, 147, 0.28)'
    };
    const [instance, accessTokenRequest] = AccessToken();

    const [actionHandlerName, setActionHandlerName] = useState<TextBoxProps["value"]>('');
    const [actionHandlerIdSelected, setActionHandlerIdSelected] = useState(-1);

    const [typeIdSelected, setTypeIdSelected] = useState<number>(-1);
    const [typeNameSelected, setTypeNameSelected] = useState<string>('');
    const [alarmMac, setAlarmMac] = useState<TextBoxProps["value"]>('');
    const [alarmIp, setAlarmIp] = useState<TextBoxProps["value"]>('');

    const [newActionHandler, setNewActionHandler] = useState<AlarmActionHandler[]>([]);
    const [actionHandlerSearchValue, setActionHandlerSearchValue] = useState<string>('');

    const [channelValue, setChannelValue] = useState<string>('');
    const [saved, setSaved] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [removed, setRemoved] = useState(false);
    const [nameMessage, setNameMessage] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<{ alarm: AlarmActionHandler; index: number } | null>(null);

    const [loading, setLoading] = useState(false);

    const [checked, setChecked] = useState(true);

    const userType = localStorage.getItem('userType');


    const handleToggleSwitch = (event: any) => {
        //console.log(event.target.value);
        setChecked(event.target.value);
    };

    const handleTestLightEvent = () => {
        const toggleValue = checked ? 1 : 0;

        window.dispatchEvent(new CustomEvent('alarmActionHandlerTestLight', {
            detail: {
                value: toggleValue,
                channel: channelValue,
                actionHandlerId: actionHandlerIdSelected
            }
        }));
    };

    const types = {
        data: [
            {
                "id": 0,
                "name": "Traffic light"
            }
        ]
    };

    const channels = {
        "data": [
            { "id": 0, "name": 'Channels 0' },
            { "id": 1, "name": 'Channels 1' },
            { "id": 2, "name": 'Channels 2' },
            { "id": 3, "name": 'Channels 3' }
        ]
    }

    useEffect(() => {
        getActionHandlerList();
    }, []);

    const hasAppliedDefaultSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedDefaultSelectionRef.current) {
            return;
        }

        if (actionHandlerIdSelected !== -1) {
            hasAppliedDefaultSelectionRef.current = true;
            return;
        }

        if (!Array.isArray(newActionHandler) || newActionHandler.length === 0) {
            return;
        }

        hasAppliedDefaultSelectionRef.current = true;
        loadSelectedAlarmActionHandler(newActionHandler[0]);
    }, [newActionHandler, actionHandlerIdSelected]);

    const withAccessToken = async (operation: (accessToken: string) => Promise<void> | void) => {
        const storedAccessToken = localStorage.getItem('accessToken');
        if (storedAccessToken) {
            await operation(storedAccessToken);
            return;
        }

        try {
            await instance.initialize();
            const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
            await operation(accessTokenResponse.accessToken);
        } catch (error: any) {
            if (error instanceof InteractionRequiredAuthError) {
                instance.acquireTokenRedirect(accessTokenRequest);
            }
            console.log(error);
        }
    };

    const resetActionHandlerForm = () => {
        setActionHandlerIdSelected(-1);
        setActionHandlerName('');
        setTypeNameSelected('');
        setTypeIdSelected(-1);
        setAlarmMac('');
        setAlarmIp('');
        setChannelValue('');
    };

    const getActionHandlerList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/subscription/actionhandler/list`, { headers })
                .then((res) => {
                    const data = res?.data?.data ?? [];
                    setNewActionHandler(data)
                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        };

        await withAccessToken(commonOperations);
    }

    const loadSelectedAlarmActionHandler = (alarm: AlarmActionHandler) => {

        setActionHandlerIdSelected(alarm.id)

        setActionHandlerName(alarm.name);
        setAlarmMac(alarm.mac);
        setAlarmIp(alarm.ipAddress);

        const selectedType = types.data.find((type: any) => type.id === alarm.type);
        setTypeIdSelected(selectedType?.id ?? -1);
        setTypeNameSelected(selectedType?.name ?? '');

        // Access the clicked element's ref
        itemRefs?.current?.forEach((element: any, i: any) => {
            if (i === alarm.id) {
                itemRefs.current[i].style.background = COLORS.accent;
                itemRefs.current[i].style.borderRadius = '10px';
                itemRefs.current[i].style.color = COLORS.white;
            } else {
                itemRefs?.current[i]?.style?.removeProperty('background')
                itemRefs?.current[i]?.style?.removeProperty('color')
            }
        });

    }

    const handleChangeAlarmActionName = useCallback((event: TextBoxChangeEvent) => {
        setActionHandlerName(event.target.value);
        //setNewAlarmActionHandler([...newAlarmActionHandler, event.target.value]);
    }, [])

    const onTypesChange = (event: any) => {
        setTypeNameSelected(event.value)

        const selectedType = types.data.find((type: any) => type.name === event.value);
        setTypeIdSelected(selectedType?.id ?? -1)
    }

    const onChannelChange = (event: any) => {
        setChannelValue(event.value)
        //setCurrentValue([...currentValue, event.target.value]);        
        //setNewSubscription([...newSubscription, event.target.value]);
    }

    const handleChangeAlarmActionMac = useCallback((event: TextBoxChangeEvent) => {
        setAlarmMac(event.target.value);
    }, []);

    const sanitizeIpInput = (value: string): string => {
        if (!value) return '';

        // solo caratteri validi
        let cleaned = value.replace(/[^0-9.]/g, '');

        // evita doppi punti
        cleaned = cleaned.replace(/\.{2,}/g, '.');

        // max 3 punti
        const parts = cleaned.split('.');
        return parts.slice(0, 4).join('.');
    };

    const isIpComplete = (value: string): boolean => {
        return /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/.test(value.trim());
    };

    const isIpInvalid = alarmIp !== '' && !isIpComplete(String(alarmIp));

    const handleChangeAlarmActionIp = useCallback((event: TextBoxChangeEvent) => {
        const rawValue = event.target.value ?? '';
        const sanitizedValue = sanitizeIpInput(String(rawValue));
        setAlarmIp(sanitizedValue);
    }, []);

    const updateActionHandlerSelected = async (idSelected: any) => {

        if (isIpInvalid) {
            return;
        }

        setLoading(true);

        const params = {
            type: typeIdSelected,
            mac: alarmMac,
            name: actionHandlerName,
            ipAddress: alarmIp
        }

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            try {
                await axios.put(`${SERVER_URL}/Subscription/ActionHandler/${idSelected}`, params, { headers })

                resetActionHandlerForm();

                setLoading(false);

                getActionHandlerList();

                setUpdated(true)
                if (!updated) {
                    setTimeout(() => {
                        setUpdated(false);
                    }, 3000);
                }

            } catch (error: any) {
                //setError(error?.message);
                setLoading(false);
            }
        };

        await withAccessToken(commonOperations);



    }

    const saveNewActionHandler = async (alarmActionName: TextBoxProps["value"]) => {

        setLoading(true);

        if (isIpInvalid) {
            setLoading(false);
            return;
        }

        const normalizedAlarmActionName = (alarmActionName ?? '').toString().trim();

        if (normalizedAlarmActionName.length === 0) {
            setNameMessage(true)
        } else {
            setNewActionHandler([...newActionHandler, {
                id: -1,
                name: normalizedAlarmActionName,
                mac: String(alarmMac ?? ''),
                ipAddress: String(alarmIp ?? ''),
                type: typeIdSelected
            }]);

            const params = {
                ipAddress: alarmIp,
                name: normalizedAlarmActionName,
                mac: alarmMac,
                type: typeIdSelected
            };

            resetActionHandlerForm();

            const commonOperations = (accessToken: string) => {
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                axios.post(`${SERVER_URL}/subscription/actionhandler`, params, { headers })
                    .then(() => {

                        setSaved(true)

                        getActionHandlerList();

                        setLoading(false);

                        setTimeout(() => {
                            setSaved(false)
                        }, 3000);

                    }).catch((data) => {
                        setLoading(false);
                        //setError(data?.message);
                    });
            };

            await withAccessToken(commonOperations);
        }

        if (saved) {
            setTimeout(() => {
                setSaved(false);
            }, 3000);
        }
        if (nameMessage) {
            setTimeout(() => {
                setNameMessage(false);
            }, 3000);
        }
    }

    const removeAlarmActionHandler = async (alarm: AlarmActionHandler, index: number) => {

        setLoading(true);

        const commonOperations = (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.delete(`${SERVER_URL}/subscription/actionhandler/${alarm.id}`, { headers })
                .then(() => {
                    setNewActionHandler((oldValues: AlarmActionHandler[]) => {
                        return oldValues.filter((_: AlarmActionHandler, i: number) => i !== index)
                    })

                    setLoading(false);
                    setRemoved(true);
                    if (!removed) {
                        setTimeout(() => {
                            setRemoved(false);
                        }, 3000);
                    }
                }).catch((data) => {
                    setLoading(false);
                });
        };

        await withAccessToken(commonOperations);
    }

    const confirmAndRemoveAlarmActionHandler = (alarm: AlarmActionHandler, index: number) => {
        setPendingDelete({ alarm, index });
        setShowDeleteDialog(true);
    }

    const closeDeleteDialog = () => {
        setShowDeleteDialog(false);
        setPendingDelete(null);
    };

    const confirmDeleteDialog = () => {
        if (pendingDelete) {
            removeAlarmActionHandler(pendingDelete.alarm, pendingDelete.index);
        }
        closeDeleteDialog();
    };

    const itemRefs: any = useRef([]);

    const bolderStyles = `
        .rules-action-handlers-root {
            gap: 12px;
            align-items: stretch;
            padding: 10px;
        }

        .rules-action-handlers-left {
            display: flex;
            flex-direction: column;
            padding: 10px;
            background: #F7F7F7;
            flex: 1.5 1 0px;
            overflow-y: hidden;
            border: 1px solid #d6e8ee;
            border-radius: 14px;
            box-shadow: 0 10px 24px rgba(10, 55, 75, 0.08);
        }

        .rules-action-handlers-right {
            border: 1px solid #cfe3ea;
            border-radius: 16px;
            box-shadow: 0 14px 30px rgba(10, 55, 75, 0.1);
            padding: 14px 16px;
            flex-grow: 2;
        }

        .rules-action-handlers-title {
            font-weight: 900;
            letter-spacing: 0.55px;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .rules-action-handler-item {
            border: 1px solid #c7dfe7;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 6px 14px rgba(10, 55, 75, 0.08);
            margin-bottom: 8px;
            padding: 4px 8px;
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .rules-action-handler-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 22px rgba(10, 55, 75, 0.15);
        }

        .rules-action-handlers-form-row {
            display: flex;
            margin-bottom: 20px;
            align-items: center;
        }

        .rules-action-handlers-label {
            width: 150px;
            align-content: center;
            font-weight: 700;
            color: #0f5f69;
            letter-spacing: 0.2px;
        }

        .rules-action-handlers-root .k-button {
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .rules-action-handlers-root .k-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(10, 55, 75, 0.16);
        }

        .rules-action-handlers-root .k-input-inner,
        .rules-action-handlers-root .k-input,
        .rules-action-handlers-root .k-dropdownlist {
            border-color: #bcd9e2;
        }

        .rules-action-handlers-root .k-input:focus-within,
        .rules-action-handlers-root .k-dropdownlist:focus-within {
            box-shadow: 0 0 0 2px rgba(1, 140, 165, 0.16);
            border-color: #018ca5;
        }

        .rules-alarm-action-handler-item {
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 6px 8px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .rules-alarm-action-handler-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }

        .rules-test-light-actions {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .rules-test-light-switch {
            min-width: 50px;
        }

        .rules-test-light-switch .k-switch .k-switch-track, .k-switch.k-switch-md .k-switch-track {
            height: 30px;
        }

        .rules-test-light-switch .k-switch-label-on,
        .rules-test-light-switch .k-switch-label-off {
            display: inline !important;
            //opacity: 1 !important;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.02em;
        }

        .rules-test-light-switch.k-switch-on .k-switch-label-on {
            color: #ffffff !important;
        }

        .rules-test-light-switch.k-switch-off .k-switch-label-off {
            color: #0f5f69 !important;
        }

        .rules-test-light-trigger {
            min-width: 44px;
            height: 32px;
            padding: 0 8px;
            border-radius: 10px;
            background-color: #198792 !important;
            border: none !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
            transition: transform 140ms ease, box-shadow 180ms ease, filter 180ms ease, background-color 180ms ease;
        }

        .rules-test-light-trigger:hover:not(:disabled) {
            transform: translateY(-1px) scale(1.03);
            background-color: #166f78 !important;
            box-shadow: 0 0 0 3px rgba(25, 135, 146, 0.2), 0 10px 20px rgba(10, 55, 75, 0.24);
            filter: saturate(1.1);
        }

        .rules-test-light-trigger:hover:not(:disabled) .rules-semaphore-icon {
            border-color: #1d7580;
            background: linear-gradient(180deg, #ffffff 0%, #d8edf2 100%);
        }

        .rules-test-light-trigger:active:not(:disabled) {
            transform: translateY(0) scale(0.98);
        }

        .rules-test-light-trigger:focus-visible {
            outline: 2px solid #9cd4dd;
            outline-offset: 2px;
        }

        .rules-semaphore-icon {
            width: 12px;
            height: 24px;
            border-radius: 8px;
            background: linear-gradient(180deg, #f7fcfe 0%, #e3f1f5 100%);
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 2px;
            padding: 2px;
        }

        .rules-semaphore-lamp {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            opacity: 0.35;
        }

        .rules-semaphore-lamp.red {
            background: #d84a4a;
        }

        .rules-semaphore-lamp.yellow {
            background: #e2b93b;
        }

        .rules-semaphore-lamp.green {
            background: #2f9f5f;
        }

        .rules-semaphore-lamp.is-on {
            opacity: 1;
            box-shadow: 0 0 5px rgba(47, 159, 95, 0.75);
        }
    `;

    const normalizedActionHandlerSearchValue = actionHandlerSearchValue.trim().toLowerCase();
    const filteredActionHandlers = normalizedActionHandlerSearchValue
        ? newActionHandler.filter((alarm: AlarmActionHandler) => {
            const alarmName = `${alarm?.name ?? ''}`.toLowerCase();
            const alarmMacValue = `${alarm?.mac ?? ''}`.toLowerCase();
            const alarmIpValue = `${alarm?.ipAddress ?? ''}`.toLowerCase();
            return alarmName.includes(normalizedActionHandlerSearchValue)
                || alarmMacValue.includes(normalizedActionHandlerSearchValue)
                || alarmIpValue.includes(normalizedActionHandlerSearchValue);
        })
        : newActionHandler;


    return (
        <div className="flex-parent-element rules-action-handlers-root">
            <style>{bolderStyles}</style>
            {loading ? loadingPanel : null}

            <br />
            <NotificationGroup
                style={{
                    right: 0,
                    bottom: 50,
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                }}
            >
                <Fade enter={true} exit={true}>
                    {saved && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setSaved(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.alarm_action_handler_saved', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {updated && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setUpdated(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.alarm_action_handler_updated', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {nameMessage && (
                        <Notification
                            type={{ style: "warning", icon: true }}
                            closable={true}
                            onClose={() => setNameMessage(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.alarm_action_handler_name_message', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {removed && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setRemoved(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.alarm_action_handler_removed', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {/*{commandMessage && (
                        <Notification
                            type={{ style: "warning", icon: true }}
                            closable={true}
                            onClose={() => setCommandMessage(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.subscription_command_type_message', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {actuatorMessage && (
                        <Notification
                            type={{ style: "warning", icon: true }}
                            closable={true}
                            onClose={() => setActuatorMessage(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.subscription_actuator_message', 'NOT_FOUND')}</span>
                        </Notification>
                    )}*/}
                    {/*{created && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setCreated(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.timeslot_updated', 'NOT_FOUND')}</span>
                        </Notification>
                    )}*/}
                </Fade>
            </NotificationGroup>

            {showDeleteDialog && (
                <Dialog
                    className="confirm-action-dialog"
                    title={localizationService.toLanguageString('configuration.rules.subscription_remove', 'NOT_FOUND')}
                    onClose={closeDeleteDialog}
                >
                    <p className="dialog-message">
                        {localizationService.toLanguageString(
                            'configuration.rules.confirm_delete_alarm_action_handler',
                            'NOT_FOUND'
                        )}
                    </p>
                    <div className="dialog-actions">
                        <Button onClick={closeDeleteDialog}>
                            {localizationService.toLanguageString('no', 'No')}
                        </Button>
                        <Button themeColor={'primary'} onClick={confirmDeleteDialog}>
                            {localizationService.toLanguageString('yes', 'Yes')}
                        </Button>
                    </div>
                </Dialog>
            )}

            {/*left panel*/}
            <div
                className="rules-action-handlers-left"
            >
                <div style={{ textAlign: 'center', marginBottom: '20px', justifyContent: 'space-between' }}>
                    <p
                        className="rules-action-handlers-title"
                        style={{ display: 'inline-flex', textAlign: 'center', fontSize: '20px', color: COLORS.title }}
                    >
                        {localizationService.toLanguageString('configuration.rules.alarm_action_handler', 'NOT_FOUND')} ({filteredActionHandlers.length})
                    </p>
                    <Button
                        title={localizationService.toLanguageString('configuration.rules.alarm_action_handler_addNew', 'NOT_FOUND')}
                        themeColor={"primary"}
                        className="plusParam"
                        style={{
                            marginRight: '10px',
                            marginTop: '0px',
                            borderRadius: '10px',
                            //boxShadow: '0 3px 10px rgba(26,136,147,0.28)'
                        }}
                        disabled={userType === 'UserAdminOnlyLocator' || userType === 'StandardOnlyLocator'}
                        onClick={() => {
                            resetActionHandlerForm();
                        }}
                    >
                        <Avatar
                            className="plusParam"
                        >
                            <SvgIcon
                                icon={plusIcon}
                                style={{ margin: '8px' }}
                            />
                        </Avatar>
                    </Button>
                </div>

                <div style={{ marginBottom: '10px' }}>
                    <TextBox
                        value={actionHandlerSearchValue}
                        onChange={(e: TextBoxChangeEvent) => setActionHandlerSearchValue(`${e.target.value ?? ''}`)}
                        placeholder={localizationService.toLanguageString('configuration.rules.alarm_action_handler_search_placeholder', 'Search actuator')}
                        style={{ width: '100%' }}
                    />
                </div>


                <div
                    className="flex-left-child-element"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        padding: '8px',
                        overflowY: 'auto',
                        width: '100%',
                        height: '88%',
                    }}
                >
                    {filteredActionHandlers?.map((alarm: AlarmActionHandler, index: number) => {
                        const isSelected = alarm.id === actionHandlerIdSelected;
                        return (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                    gap: '8px', 
                                    cursor: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 'default' : 'pointer'}`,
                                }}
                                >
                                <div
                                    className="trash-icon"
                                    style={{
                                        float: 'left',
                                        cursor: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 'default' : 'pointer'}`,
                                        alignContent: 'center',
                                        pointerEvents: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 'none' : 'auto'}`,
                                        opacity: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 0.5 : 1}`
                                    }}
                                    title={localizationService.toLanguageString('configuration.rules.subscription_remove', 'NOT_FOUND')}
                                    onClick={() => confirmAndRemoveAlarmActionHandler(alarm, index)}
                                >
                                    <Avatar
                                        className="trash-icon"
                                    >
                                        <SvgIcon icon={trashIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                                    </Avatar>
                                </div>

                                <p
                                    className="rules-alarm-action-handler-item"
                                    ref={(el) => (itemRefs.current[alarm.id] = el)}
                                    style={{
                                        cursor: 'pointer',
                                        margin: '0px',
                                        padding: '10px',
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        float: 'left',
                                        alignContent: 'center',
                                        paddingRight: '10px',
                                        fontSize: `${alarm.name != '' ? 'clamp(10px, 0.8vw, 16px)' : 'clamp(9px, 0.7vw, 16px)'}`,
                                        pointerEvents: `${userType === 'StandardOnlyLocator' ? 'none' : 'auto'}`,
                                        opacity: `${userType === 'StandardOnlyLocator' ? 0.5 : 1}`,
                                        background: isSelected ? COLORS.selectedBg : COLORS.white
                                    }}
                                    onClick={() => loadSelectedAlarmActionHandler(alarm)}
                                    >
                                    {alarm.name}
                                </p>
                            </div>
                        )
                    })}
                </div>

            </div>

            {/*right panel*/}
            <div
                className="flex-right-child-element rules-action-handlers-right"
                style={{
                    background: COLORS.white,
                    borderRadius: '12px',
                    border: '1px solid #cfe3ea',
                    padding: '16px',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
                }}
            >
                <div>
                    <div className="rules-action-handlers-form-row">
                        <div className="rules-action-handlers-label">
                            {localizationService.toLanguageString('configuration.rules.alarm_action_handler_name', 'NOT_FOUND')}
                        </div>
                        <div style={{ display: 'flex', width: '350px' }}>
                            <TextBox
                                style={{ width: "350px", marginRight: '10px' }}
                                value={actionHandlerName != '' ? actionHandlerName : ''}
                                onChange={handleChangeAlarmActionName}
                                placeholder={localizationService.toLanguageString('configuration.rules.alarm_action_handler_name', 'NOT_FOUND')}
                                disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                            />

                            {actionHandlerIdSelected === -1 ?
                                //save 
                                <Button
                                    title={localizationService.toLanguageString('configuration.rules.alarm_action_handler_save', 'NOT_FOUND')}
                                    themeColor={"primary"}
                                    className="saveParam"
                                    disabled={actionHandlerName == '' ? true : false || typeNameSelected.length == 0 ? true : false || isIpInvalid || userType === 'UserAdminOnlyLocator' || userType === 'StandardOnlyLocator'}
                                    onClick={() => saveNewActionHandler(actionHandlerName)}
                                >
                                    <Avatar
                                        className="saveParam"
                                    >
                                        <SvgIcon
                                            icon={saveIcon}
                                            style={{ margin: '8px' }}
                                        />
                                    </Avatar>
                                </Button>
                                :
                                // add & updated 
                                <div style={{ display: 'inline' }} >
                                    <Button
                                        title={localizationService.toLanguageString('configuration.rules.alarm_action_handler_update', 'NOT_FOUND')}
                                        themeColor={"primary"}
                                        className="saveParam"
                                        style={{
                                            marginRight: '10px'
                                        }}
                                        disabled={actionHandlerName == '' ? true : false || isIpInvalid || userType === 'UserAdminOnlyLocator' || userType === 'StandardOnlyLocator'}
                                        onClick={() => updateActionHandlerSelected(actionHandlerIdSelected)}
                                    >
                                        <Avatar
                                            className="saveParam"
                                        >
                                            <SvgIcon
                                                icon={saveIcon}
                                                style={{ margin: '8px' }}
                                            />
                                        </Avatar>
                                    </Button>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="rules-action-handlers-form-row">
                        <div className="rules-action-handlers-label">
                            {localizationService.toLanguageString('configuration.rules.alarm_action_handler_type', 'NOT_FOUND')}
                        </div>
                        <DropDownList
                            style={{
                                width: "350px",
                            }}
                            data={types.data.map((type: any) => {
                                return type.name
                            })}
                            value={typeNameSelected}
                            onChange={onTypesChange}
                            disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                        />
                    </div>

                    <div className="rules-action-handlers-form-row">
                        <div className="rules-action-handlers-label">
                            {localizationService.toLanguageString('configuration.rules.alarm_action_handler_mac', 'NOT_FOUND')}
                        </div>
                        <TextBox
                            style={{ width: "350px" }}
                            value={alarmMac !== '' ? alarmMac : ''}
                            onChange={handleChangeAlarmActionMac}
                            placeholder={localizationService.toLanguageString('configuration.rules.alarm_action_handler_mac', 'NOT_FOUND')}
                            disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                        />
                    </div>

                    <div className="rules-action-handlers-form-row">
                        <div className="rules-action-handlers-label">
                            {localizationService.toLanguageString('configuration.rules.alarm_action_handler_ip', 'NOT_FOUND')}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <TextBox
                                style={{ width: "350px" }}
                                value={alarmIp !== '' ? alarmIp : ''}
                                onChange={handleChangeAlarmActionIp}
                                placeholder={localizationService.toLanguageString('configuration.rules.alarm_action_handler_ip', 'NOT_FOUND')}
                                disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                            />
                            {alarmIp !== '' && !isIpComplete(String(alarmIp)) && (
                                <span style={{ color: COLORS.markerRed, fontSize: '12px', marginTop: '4px' }}>
                                    {localizationService.toLanguageString('configuration.rules.alarm_action_handler_ip_invalid', 'NOT_FOUND')}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="rules-action-handlers-form-row">
                        <div className="rules-action-handlers-label">
                            {localizationService.toLanguageString('configuration.rules.alarm_action_handler_test_light', 'NOT_FOUND')}
                        </div>
                        <div>
                            <div className="rules-test-light-actions" >
                                <DropDownList
                                    style={{
                                        width: "225px",
                                        marginRight: '10px'
                                    }}
                                    data={channels.data?.map((channel: any) => {
                                        return (
                                            channel.name
                                        )
                                    })}
                                    value={channelValue}
                                    onChange={onChannelChange}
                                    disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                                />

                                <Switch
                                    className="rules-test-light-switch"
                                    onChange={handleToggleSwitch}
                                    checked={checked}
                                    onLabel={'1'}
                                    offLabel={'0'}
                                    disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                                />

                                <Button
                                    title={localizationService.toLanguageString('configuration.rules.alarm_action_handler_test_light', 'NOT_FOUND')}
                                    className="rules-test-light-trigger"
                                    style={{ lineHeight: 0 }}
                                    themeColor={'primary'}
                                    onClick={handleTestLightEvent}
                                    disabled={userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator'}
                                >
                                    <span className="rules-semaphore-icon" aria-hidden="true">
                                        <span className={`rules-semaphore-lamp red is-on`} />
                                        <span className="rules-semaphore-lamp yellow is-on" />
                                        <span className={`rules-semaphore-lamp green is-on`} />
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>                    

                </div>
            </div>
        </div>
    )
}
